// Typed fetch client for the platform-graph service, operator surface
// (plan madde 6.5).
//
// Tip safety chain: services/platform-graph route's `schema:` block
// (Zod) → @fastify/swagger `openapi.yaml` (3.1 spec) → openapi-typescript
// `src/api/types.gen.ts` (paths interface) → openapi-fetch typed client
// (this file) → React hooks (TanStack Query callers).
//
// Adding a new /ops/v1/* endpoint at madde 3:
//   1. Backend route declares `schema: { ... }` (plan madde 6.1 pilot
//      pattern; healthz + ops-me are the two canonical examples).
//   2. `pnpm --filter @novemai/platform-graph openapi:dump`
//      regenerates services/platform-graph/openapi.yaml.
//   3. `pnpm --filter @novemai/operator-console codegen`
//      regenerates src/api/types.gen.ts.
//   4. Drift tests in BOTH workspaces (openapi:check + types.gen.test.ts)
//      fail in CI if either step is skipped — the artifacts MUST move
//      together.
//   5. Frontend hooks call `operatorGraph.GET("/ops/v1/<endpoint>")`
//      and the IDE autocompletes the path + types the response.

import createClient from "openapi-fetch";

import { resolveOperatorPlatformGraphBaseUrl } from "@/config/operator-deployment";

import { unwrap } from "./envelope";
import type { paths } from "./types.gen";

// Resolve at module initialization, before openapi-fetch can construct a
// request and before the interceptor below can attach an operator token.
export const operatorGraphBaseUrl = resolveOperatorPlatformGraphBaseUrl({
  configuredUrl: import.meta.env["VITE_OPERATOR_PLATFORM_GRAPH_BASE_URL"],
  mode: import.meta.env.PROD ? "production" : "development",
});
const baseUrl = operatorGraphBaseUrl;

export const operatorGraph = createClient<paths>({
  baseUrl,
  // OIDC cookies + W3C traceparent forwarding (ADR-010). Trace context
  // injection itself wires at plan madde 7.2; the credentials:include
  // flag is the cross-origin cookie precondition for it.
  credentials: "include",
});

// The root auth bridge binds the current user before routed queries mount.
// Keeping the token in memory also lets a successful renew bind its result
// before React processes USER_LOADED and before a rejected GET is replayed.
let accessToken: string | null = null;
let authExpiredHandler: (() => Promise<void>) | null = null;
let recovery: Promise<void> | null = null;

export function setOperatorAccessToken(token: string | null): void {
  accessToken = token;
}

export function setOperatorAuthExpiredHandler(handler: (() => Promise<void>) | null): void {
  authExpiredHandler = handler;
}

operatorGraph.use({
  onRequest({ request }) {
    if (accessToken !== null) request.headers.set("Authorization", `Bearer ${accessToken}`);
    return request;
  },
  async onResponse({ request, response, options }) {
    const rejectedBearer = request.headers.get("Authorization");
    if (response.status !== 401 || accessToken === null || rejectedBearer === null) return response;

    // A delayed response from the previous token does not start a second
    // renew. Concurrent failures of the current token share one renewal.
    if (rejectedBearer === `Bearer ${accessToken}` && authExpiredHandler !== null) {
      if (recovery === null) {
        const running = authExpiredHandler().catch(() => undefined);
        recovery = running;
        void running.finally(() => { if (recovery === running) recovery = null; });
      }
      await recovery;
    }
    if (
      accessToken === null ||
      rejectedBearer === `Bearer ${accessToken}` ||
      (request.method !== "GET" && request.method !== "HEAD") ||
      request.signal.aborted
    ) return response;

    // Retry a safe read once with the explicit fresh token. Calling the
    // underlying transport skips this middleware, bounding a second 401.
    // Writes always return their original failure for explicit user retry.
    const retry = new Request(request);
    retry.headers.set("Authorization", `Bearer ${accessToken}`);
    return options.fetch(retry);
  },
});

/**
 * Healthz round-trip helper used by the home page query (plan madde 1
 * scaffold). Returns the typed `data` payload via the canonical
 * envelope unwrap helper (plan madde 8.1). Throws `EnvelopeError`
 * on any failure or malformed response — the toast adapter at plan
 * madde 8.2 / TanStack Query's error state surfaces it to the user.
 */
export async function fetchHealthz(): Promise<
  NonNullable<
    paths["/v1/healthz"]["get"]["responses"][200]["content"]["application/json"]["data"]
  >
> {
  return unwrap(operatorGraph.GET("/v1/healthz"));
}
